/**
 * User Management Tab
 * Main container for admin user management interface
 */

import { LoadingSpinner } from '@components/ui';
import { useAdminUsers } from '@hooks/useAdminUsers';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { EmailSuppressionsTab } from './EmailSuppressionsTab';
import { InviteManagementTab } from './InviteManagementTab';
import { UserAnalyticsTab } from './UserAnalyticsTab';
import { UserDetailModal } from './UserDetailModal';
import { UserFilters } from './UserFilters';
import { UserTable } from './UserTable';

// =============================================================================
// Component
// =============================================================================

export function UserManagementTab(): ReactElement {
  const [activeSubTab, setActiveSubTab] = useState<
    'management' | 'analytics' | 'invites' | 'suppressions'
  >('management');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { users, total, pages, loading, error, refetch } = useAdminUsers({
    page,
    limit: 50,
    search,
    role: roleFilter,
    status: statusFilter,
  });

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pages) {
      setPage(newPage);
    }
  };

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setPage(1); // Reset to first page on search
  };

  const handleRoleFilterChange = (newRole: string) => {
    setRoleFilter(newRole);
    setPage(1); // Reset to first page on filter change
  };

  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setPage(1); // Reset to first page on filter change
  };

  const handleUserUpdate = () => {
    refetch();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-theme-text">Users</h2>
        <p className="text-sm text-theme-text-secondary">
          User management, analytics & permissions
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="flex flex-wrap justify-center gap-2">
        <button
          type="button"
          onClick={() => setActiveSubTab('management')}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            activeSubTab === 'management'
              ? 'bg-theme-primary-fill text-theme-on-primary-fill'
              : 'bg-theme-surface text-theme-text-secondary hover:bg-theme-bg-secondary'
          }`}
        >
          Management
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('analytics')}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            activeSubTab === 'analytics'
              ? 'bg-theme-primary-fill text-theme-on-primary-fill'
              : 'bg-theme-surface text-theme-text-secondary hover:bg-theme-bg-secondary'
          }`}
        >
          Analytics
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('invites')}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            activeSubTab === 'invites'
              ? 'bg-theme-primary-fill text-theme-on-primary-fill'
              : 'bg-theme-surface text-theme-text-secondary hover:bg-theme-bg-secondary'
          }`}
        >
          Invites
        </button>
        <button
          type="button"
          onClick={() => setActiveSubTab('suppressions')}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            activeSubTab === 'suppressions'
              ? 'bg-theme-primary-fill text-theme-on-primary-fill'
              : 'bg-theme-surface text-theme-text-secondary hover:bg-theme-bg-secondary'
          }`}
        >
          Email Suppressions
        </button>
      </div>

      {/* Render active sub-tab */}
      {activeSubTab === 'analytics' ? (
        <UserAnalyticsTab />
      ) : activeSubTab === 'invites' ? (
        <InviteManagementTab />
      ) : activeSubTab === 'suppressions' ? (
        <EmailSuppressionsTab />
      ) : (
        <>
          <div className="text-center text-sm text-theme-text-secondary">
            <span className="font-semibold text-theme-text">{total}</span> total users
          </div>

          {/* Filters */}
          <UserFilters
            search={search}
            onSearchChange={handleSearchChange}
            roleFilter={roleFilter}
            onRoleFilterChange={handleRoleFilterChange}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
          />

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="p-4 bg-theme-danger/10 border border-theme-danger rounded-lg text-theme-danger">
              {error}
            </div>
          )}

          {/* Table */}
          {!loading && !error && (
            <>
              <div className="bg-theme-surface rounded-xl border border-theme-border overflow-hidden">
                <UserTable users={users} onViewUser={setSelectedUserId} />
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="flex items-center justify-between">
                  <div className="text-sm text-theme-text-secondary">
                    Page {page} of {pages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                      className="px-4 py-2 bg-theme-bg border border-theme-border text-theme-text rounded-lg hover:bg-theme-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Previous</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === pages}
                      className="px-4 py-2 bg-theme-bg border border-theme-border text-theme-text rounded-lg hover:bg-theme-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      <span>Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* User Detail Modal */}
          {selectedUserId && (
            <UserDetailModal
              userId={selectedUserId}
              onClose={() => setSelectedUserId(null)}
              onUpdate={handleUserUpdate}
            />
          )}
        </>
      )}
    </div>
  );
}
